import { useState, useCallback, useEffect, useRef } from "react";
import { message } from "antd";
import { FmvIndexComponent } from "../components/fmv/FmvIndexComponent";
import type { FmvRecord } from "../components/fmv/FmvTableComponent";
import { useVehicles } from "../hooks/vehicles/useVehicles";

export const FmvPage = () => {
  const {
    vehicles,
    meta,
    loading,
    fetchVehicles,
    updateVehicle,
    bulkUpsert,
    refetch,
  } = useVehicles();

  const [selectedRow, setSelectedRow] = useState<FmvRecord | null>(null);
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchVehicles({ search: searchText || undefined, page: 1 });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText, fetchVehicles]);

  // Modal states
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      fetchVehicles({ page, per_page: pageSize });
    },
    [fetchVehicles]
  );

  const onSelectRow = useCallback((record: FmvRecord | null) => {
    setSelectedRow(record);
    setSelectedRowKey(record ? record.BMTYTL : null);
  }, []);

  // ── Update handlers ──
  const onOpenUpdateModal = useCallback(() => {
    if (!selectedRow) {
      message.warning("Please select a row first.");
      return;
    }
    setUpdateModalOpen(true);
  }, [selectedRow]);

  const onCloseUpdateModal = useCallback(() => {
    setUpdateModalOpen(false);
  }, []);

  const onSaveUpdate = useCallback(
    async (updatedRecord: FmvRecord) => {
      if (!updatedRecord.vehicle_id) {
        message.error("Cannot update: missing vehicle ID.");
        return;
      }

      setUpdateLoading(true);
      const success = await updateVehicle(
        updatedRecord.vehicle_id,
        updatedRecord
      );

      if (success) {
        setUpdateModalOpen(false);
        setSelectedRow(null);
        setSelectedRowKey(null);
      }
      setUpdateLoading(false);
    },
    [updateVehicle]
  );

  // ── Bulk Upload handlers ──
  const onOpenBulkUploadModal = useCallback(() => {
    setBulkUploadModalOpen(true);
  }, []);

  const onCloseBulkUploadModal = useCallback(() => {
    setBulkUploadModalOpen(false);
  }, []);

  const onConfirmBulkUpload = useCallback(
    async (records: FmvRecord[]) => {
      setBulkUploadLoading(true);
      const result = await bulkUpsert(records);
      if (result) {
        setSelectedRow(null);
        setSelectedRowKey(null);
      }
      setBulkUploadLoading(false);
      return result;
    },
    [bulkUpsert]
  );

  return (
    <FmvIndexComponent
      data={vehicles}
      loading={loading}
      meta={meta}
      onPageChange={onPageChange}
      selectedRow={selectedRow}
      selectedRowKey={selectedRowKey}
      onSelectRow={onSelectRow}
      searchText={searchText}
      onSearchChange={setSearchText}
      updateModalOpen={updateModalOpen}
      onOpenUpdateModal={onOpenUpdateModal}
      onCloseUpdateModal={onCloseUpdateModal}
      onSaveUpdate={onSaveUpdate}
      updateLoading={updateLoading}
      bulkUploadModalOpen={bulkUploadModalOpen}
      onOpenBulkUploadModal={onOpenBulkUploadModal}
      onCloseBulkUploadModal={onCloseBulkUploadModal}
      onConfirmBulkUpload={onConfirmBulkUpload}
      bulkUploadLoading={bulkUploadLoading}
      onRefresh={refetch}
    />
  );
};
